import { useState } from 'react';
import { useT } from '../i18n';
import { useLive } from '../live/store';
import { ago } from '../lib/format';
import { autoLocateOf, leaveCharger, localizationOf, locateAuto, setAutoLocate } from '../lib/localization';

/** Localization state line: located / locating… / not located (+ last error) / blocked by charging (+ Leave charger). */
export function LocalizationState({ serial, compact }: { serial: string; compact?: boolean }) {
  const t = useT();
  const st = useLive((s) => s.robots[serial]?.status);
  const loc = localizationOf(st);
  const charging = Boolean(st?.charging);
  const blocked = loc.state === 'blocked_charging' || (loc.state !== 'located' && charging);
  return (
    <span className={`loc-state${compact ? ' compact' : ''}`}>
      {loc.state === 'locating' ? (
        <>
          <span className="spinner sm" aria-hidden />
          <span style={{ color: 'var(--accent)' }}>{t('loc.locating')}</span>
        </>
      ) : blocked ? (
        <>
          <span style={{ color: 'var(--red-soft)' }}>{t('loc.blockedCharging')}</span>
          <button type="button" className="btn xs" onClick={() => void leaveCharger(serial)}>
            {t('loc.leaveCharger')}
          </button>
        </>
      ) : loc.state === 'located' ? (
        <span style={{ color: 'var(--green)' }}>{t('loc.located')}</span>
      ) : (
        <span style={{ color: 'var(--accent)' }}>{t('mapRobots.notLocated')}</span>
      )}
      {loc.lastError && loc.state !== 'located' && (
        <span className="loc-error" title={loc.lastError}>
          {loc.lastError}
          {loc.lastTryAt ? ` · ${ago(loc.lastTryAt)}` : ''}
        </span>
      )}
    </span>
  );
}

/** "Locate automatically" button + "Auto-locate" toggle (setSettings autoLocate). */
export function LocalizationActions({ serial, online, size = 'xs' }: { serial: string; online: boolean; size?: 'xs' | 'sm' }) {
  const t = useT();
  const st = useLive((s) => s.robots[serial]?.status);
  const reported = autoLocateOf(st);
  const [optimistic, setOptimistic] = useState<boolean | undefined>(undefined);
  const auto = optimistic ?? reported ?? false;
  const locating = localizationOf(st).state === 'locating';
  const [busy, setBusy] = useState(false);
  return (
    <>
      <button
        type="button"
        className={`btn ${size}`}
        disabled={!online || busy || locating}
        onClick={async () => {
          setBusy(true);
          await locateAuto(serial);
          setBusy(false);
        }}
        title={t('loc.autoTitle')}
      >
        {busy || locating ? t('loc.locating') : t('loc.auto')}
      </button>
      <label className="row loc-toggle" title={t('loc.autoLocateTitle')}>
        <input
          type="checkbox"
          checked={auto}
          disabled={!online}
          onChange={async (e) => {
            const on = e.target.checked;
            setOptimistic(on);
            const ok = await setAutoLocate(serial, on);
            setOptimistic(ok ? on : undefined);
          }}
        />
        {t('loc.autoLocate')}
      </label>
    </>
  );
}
